import { AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { SignalRService } from '../../../core/services/signalR.service';
import { Subject } from 'rxjs/internal/Subject';
import { TagDTO } from '@sb-tools/typescript/models/dto/tag.dto';
import { DetailedTicketDTO } from '../../../core/models/dto/detailed-ticket.dto';
import { TicketUpdatedEventModel } from '../../../core/models/dto/signalR/ticket-updated-event-model.model';
import { AppDTO } from '@sb-tools/typescript/models/dto/app.dto';
import { LoaderComponent } from '../../../../sb-tools/typescript/components/loader/loader.component';
import { NgClass } from '@angular/common';

@Component({
    selector: 'app-application',
    templateUrl: './application.component.html',
    styleUrls: ['./application.component.scss'],
    imports: [LoaderComponent, NgClass],
})
export class ApplicationComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('appIFrame') iframe: ElementRef;

    @HostListener('window:message', ['$event']) onPostMessage(event) {
        event.preventDefault();

        if (event.data.isNeedSetInitData) {
            this.isIframeReceivedAllNecessaryData = true;
            this.currentApp = { ...this.currentApp, isNeedGetInitData: false };

            this.iframe.nativeElement.contentWindow.postMessage(
                Object.assign(
                    {
                        init: this.currentApp.name,
                        isNeedGetInitData: this.currentApp.isNeedGetInitData,
                    },
                    this.setDataForApps(event.data.requiredData),
                ),
                this.currentApp.inboxUrl,
            );
        }
    }

    @Input() currentApp: AppDTO;
    @Input() ticket: DetailedTicketDTO;

    currentAppId: number;
    isInitialized = false;
    isIframeReceivedAllNecessaryData = false;

    private unsubscriber = new Subject<void>();

    constructor(
        public commonDataService: CommonDataService,
        private sanitizer: DomSanitizer,
        private signalRService: SignalRService,
    ) {}

    ngOnInit() {
        this.currentApp.safeInboxUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.currentApp.inboxUrl);
        this.listenTicketUpdatedEvent();
    }

    ngAfterViewInit() {
        this.isInitialized = true;
    }

    loadIframe(iframe) {
        if (this.isInitialized) {
            iframe.contentWindow.postMessage(
                {
                    init: this.currentApp.name,
                    isNeedGetInitData: this.currentApp.isNeedGetInitData,
                    language: this.commonDataService.user.localizationLanguage,
                    theme: this.commonDataService.user.theme,
                },
                this.currentApp.inboxUrl,
            );
        }
    }

    private getCommunityName() {
        return this.ticket.communityName;
    }

    private getTeamMemberId() {
        return this.commonDataService.user.id;
    }

    private getTicketAuthorId() {
        return this.ticket.firstMessageCreator.id;
    }

    private getTicketAuthorEmail() {
        return this.ticket.firstMessageCreator.email;
    }

    private getTicketAuthorName() {
        return this.ticket.firstMessageCreator.name;
    }

    private getTicketAuthorImage() {
        return this.ticket.firstMessageCreator.image;
    }

    private getTicketCategory() {
        if (!this.ticket.category) {
            return null;
        }

        return {
            id: this.ticket.category.id,
            value: this.ticket.category.name,
        };
    }

    private getTicketContent() {
        return location.href;
    }

    private getTicketId() {
        return this.ticket.id;
    }

    private getTicketStatus() {
        return this.ticket.status;
    }

    private getTicketSubject() {
        return this.ticket.title;
    }

    private getTicketTags() {
        return this.ticket.tags.map((tag: TagDTO) => {
            return {
                id: tag.id,
                value: tag.text,
            };
        });
    }

    private getIsNeedConfirmAuth() {
        return this.currentApp?.isNeedGetInitData;
    }

    private listenTicketUpdatedEvent() {
        this.signalRService
            .onTicketUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((updatedTicket: TicketUpdatedEventModel) => {
                if (updatedTicket.ticket.id === this.ticket?.id) {
                    this.iframe.nativeElement.contentWindow.postMessage(
                        Object.assign(
                            {
                                init: this.currentApp.name,
                                isNeedGetInitData: false,
                            },
                            this.setDataForApps([
                                'CommunityName',
                                'TeamMemberId',
                                'TicketAuthorId',
                                'TicketAuthorEmail',
                                'TicketAuthorName',
                                'TicketAuthorImage',
                                'TicketCategory',
                                'TicketContent',
                                'TicketId',
                                'TicketStatus',
                                'TicketSubject',
                                'TicketTags',
                            ]),
                        ),
                        this.currentApp.inboxUrl,
                    );
                }
            });
    }

    private setDataForApps(requiredData: string[]): any {
        const appData = {};

        requiredData.forEach((data) => {
            if (!this[`get${data}`]) {
                return;
            }
            appData[data] = this[`get${data}`]();
        });

        return appData;
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
